//
// Copyright (c) 2026 Comcast
//

import { Link, useNavigate } from 'react-router'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import supportsWebP from 'supports-webp'

import ErrorBackground4KWebp from 'assets/img/ErrorBG3840x2160.webp'
import ErrorBackground4KJpg from 'assets/img/ErrorBG3840x2160.jpg'
import ErrorBackground2KWebp from 'assets/img/ErrorBG2560x1440.webp'
import ErrorBackground2KJpg from 'assets/img/ErrorBG2560x1440.jpg'
import ErrorBackgroundFHDWebp from 'assets/img/ErrorBG1920x1080.webp'
import ErrorBackgroundFHDJpg from 'assets/img/ErrorBG1920x1080.jpg'

import colors from 'components/utils/colors'
import Footer from 'components/App/Footer/Index'
import IconLink from 'components/Shared/IconLink/Index'
import Notification from 'components/Shared/Notification/Index'

const StyledWrapper = styled.div`
  height: 100%;
  width: 100%;
  @media (max-width: 1920px) {
    background-image: url(${supportsWebP ? ErrorBackgroundFHDWebp : ErrorBackgroundFHDJpg});
  }
  @media (min-width: 1921px) and (max-width: 2560px) {
    background-image: url(${supportsWebP ? ErrorBackground2KWebp : ErrorBackground2KJpg});
  }
  @media (min-width: 2561px) {
    background-image: url(${supportsWebP ? ErrorBackground4KWebp : ErrorBackground4KJpg});
  }
  background-color: ${colors.secondary95};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`

const StyledContent = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  left: 10%;
  top: 10%;
  width: 40%;
`

const StyledHeader = styled.h1`
  font-family: Exo;
  font-size: 36px;
  font-weight: 500;
  color: #0A8BC2;
  letter-spacing: -0.36px;
  line-height: 49px;
  padding-bottom: 9px;
`

const ErrorCode = styled.div`
  font-family: Exo;
  font-size: 16px;
  font-weight: bold;
  color: ${colors.secondary40};
  line-height: 22px;
  padding-bottom: 3px;
`

const ErrorText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.secondary40};
  line-height: 22px;
  padding-bottom: 15px;
`

const StyledIconLink = styled(IconLink)`
  height: 30px;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.primary35};
  line-height: 26px;

  &:before {
    color: ${colors.secondary60} !important;
  }
  &:hover:before {
    color: ${colors.primary40} !important;
  }
`

const ErrorPage = ({ code, errorText, showBackButton }) => {
  const navigate = useNavigate()

  const onBackClick = () => {
    navigate(-1)
  }

  return (
    <StyledWrapper>
      <Notification />
      <StyledContent>
        <StyledHeader>Oops...</StyledHeader>
        <ErrorCode data-test='error-code'>{code}</ErrorCode>
        <ErrorText data-test='error-text'>{errorText}</ErrorText>

        {showBackButton && <StyledIconLink iconName='icon-arrow-left' text='Back' onClick={onBackClick} />}
        <Link to='/'>
          <StyledIconLink iconName='icon-arrow-left' text='Home' />
        </Link>
      </StyledContent>

      <Footer />
    </StyledWrapper>
  )
}

ErrorPage.propTypes = {
  code: PropTypes.string.isRequired,
  errorText: PropTypes.node,
  showBackButton: PropTypes.bool,
}

export default ErrorPage
